import { first, throttle } from 'underscore';
import React, { createContext, useEffect, useCallback } from 'react';
import { getLayoutSize, LayoutSize } from './layout-size';

interface LayoutSizeContextProps {
  layoutSize: LayoutSize;
  contentRect: number;
}

const initialLayoutSizeContext: LayoutSizeContextProps = {
  layoutSize: undefined,
  contentRect: undefined,
};

export const LayoutSizeContext = createContext<LayoutSizeContextProps>(null);

/**
 * HOC to provide layout size context to a component
 * @param {React.FC} WrappedComponent Component to wrap
 * @param {number} observationDelay Delay in ms to throttle resize observer, defaults to 10ms
 * @returns {React.FC} Wrapped component with layout size provider
 */
export const withLayoutSizeContext = (WrappedComponent, observationDelay = 10) => {
  const HOC = (props) => {
    const [layoutSize, setLayoutSize] = React.useState(initialLayoutSizeContext);

    const onObservation = useCallback(throttle((entries) => {
      const entry = first(entries);

      if (entry) {
        const newLayoutSize = getLayoutSize(entry.contentRect.width);

        setLayoutSize({
          layoutSize: newLayoutSize,
          contentRect: entry.contentRect,
        });
      }
    }, observationDelay), [observationDelay]);

    useEffect(() => {
      const resizeObserver = new (window as any).ResizeObserver(onObservation);

      resizeObserver.observe(document.body);

      return () => {
        resizeObserver.disconnect();
      };
    }, [onObservation, setLayoutSize]);

    return (
      <LayoutSizeContext.Provider value={layoutSize}>
        <WrappedComponent {...props} />
      </LayoutSizeContext.Provider>
    );
  };

  return HOC;
};

export const useLayoutSizeContext = () => React.useContext(LayoutSizeContext);
